import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Chip,
  Box,
  Paper,
  Divider,
  Grid,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  MenuBook as PublicationIcon,
  MicNone as PresentationIcon,
} from "@mui/icons-material";

const honors = [
  {
    title: "Nexus DevDays Award Winner",
    org: "Nexus DevDays 2026",
    date: "March 2026",
    emoji: "🥇",
  },
  {
    title: "MLH Best Use of Solana",
    org: "HackNYU",
    date: "November 2025",
    emoji: "🏅",
  },
  {
    title: "MMR Group Technology Scholarship",
    org: "Southeastern Louisiana University",
    date: "2026",
    emoji: "🎓",
  },
  {
    title: "Thomas F. Higginbotham Scholarship",
    org: "Southeastern Louisiana University",
    date: "2025–2026",
    emoji: "🎓",
  },
  {
    title: "UPE Scholarship (Honors) Award",
    org: "Southeastern Louisiana University",
    date: "Spring 2025",
    emoji: "🎓",
  },
  {
    title: "DXC Technology Intern AI Challenge Winner",
    org: "DXC Technology",
    date: "May 2024",
    emoji: "🏆",
  },
  {
    title: "President's List & Priority Scholarship",
    org: "Southeastern Louisiana University",
    date: "Fall 2022–2025",
    emoji: "⭐",
  },
  {
    title: "Association for Computing Machinery (ACM)",
    org: "Southeastern Louisiana University",
    date: "2023–2025",
    emoji: "💻",
  },
];

const publications = [
  {
    title: "ConnectMe: QR-Powered Digital Business Cards",
    venue: "International Journal of Modern Engineering Research (IJMER)",
    date: "2024",
    type: "Publication",
    url: "https://www.ijmer.com/papers/Vol14_issue6/1406106111.pdf",
  },
  {
    title: "E-Library Project",
    venue: "ULL Summit & ULS Conference",
    date: "2024",
    type: "Presentation",
  },
];

export const AwardsSection = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        mb: 4,
        borderRadius: 4,
        border: "1px solid var(--border-color)",
        background: "var(--card-bg)",
        boxShadow: "0 8px 32px rgba(102,126,234,0.08)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Honors Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(245,158,11,0.35)",
          }}
        >
          <TrophyIcon sx={{ color: "white", fontSize: 21 }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: "var(--body_color)" }}
        >
          Honors & Involvement
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={1.5} sx={{ mb: 4 }}>
        {honors.map((award, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.8,
                borderRadius: 2.5,
                border: "1px solid var(--border-color)",
                background: "var(--card-bg)",
                gap: 1.5,
                transition: "all 0.25s ease",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
                "&:hover": {
                  transform: "translateX(5px)",
                  borderColor: "rgba(245,158,11,0.35)",
                  boxShadow: "0 4px 14px rgba(245,158,11,0.15)",
                  background:
                    "linear-gradient(90deg, rgba(245,158,11,0.05) 0%, transparent 100%)",
                },
              }}
            >
              <Typography sx={{ fontSize: "1.3rem", flexShrink: 0 }}>
                {award.emoji}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="700"
                  sx={{
                    color: "var(--body_color)",
                    fontSize: "0.83rem",
                    lineHeight: 1.3,
                  }}
                >
                  {award.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--body_color)",
                    opacity: 0.55,
                    fontSize: "0.7rem",
                    display: "block",
                  }}
                >
                  {award.org}
                </Typography>
              </Box>
              <Chip
                label={award.date}
                size="small"
                sx={{
                  fontSize: "0.65rem",
                  bgcolor: "rgba(245,158,11,0.1)",
                  color: "#d97706",
                  fontWeight: 600,
                  border: "1px solid rgba(245,158,11,0.2)",
                  flexShrink: 0,
                  height: 20,
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Publications Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #667eea 0%, #06b6d4 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            flexShrink: 0,
            boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
          }}
        >
          <PublicationIcon sx={{ color: "white", fontSize: 21 }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: "var(--body_color)" }}
        >
          Publications & Presentations
        </Typography>
      </Box>
      <Divider sx={{ mb: 2.5 }} />

      <Grid container spacing={2}>
        {publications.map((pub, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Box
              component={pub.url ? "a" : "div"}
              href={pub.url}
              target={pub.url ? "_blank" : undefined}
              rel={pub.url ? "noopener noreferrer" : undefined}
              sx={{
                display: "block",
                p: 2.5,
                borderRadius: 3,
                border: "1px solid var(--border-color)",
                background:
                  "linear-gradient(135deg, rgba(102,126,234,0.04) 0%, rgba(6,182,212,0.04) 100%)",
                textDecoration: "none",
                transition: "all 0.3s ease",
                height: "100%",
                cursor: pub.url ? "pointer" : "default",
                "&:hover": pub.url
                  ? {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(102,126,234,0.15)",
                      borderColor: "rgba(102,126,234,0.3)",
                    }
                  : {},
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background:
                      pub.type === "Publication"
                        ? "linear-gradient(135deg, rgba(102,126,234,0.15) 0%, rgba(6,182,212,0.15) 100%)"
                        : "linear-gradient(135deg, rgba(118,75,162,0.15) 0%, rgba(240,147,251,0.15) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {pub.type === "Publication" ? (
                    <PublicationIcon sx={{ fontSize: 18, color: "#667eea" }} />
                  ) : (
                    <PresentationIcon sx={{ fontSize: 18, color: "#764ba2" }} />
                  )}
                </Box>
                <Box>
                  <Chip
                    label={pub.type}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      height: 18,
                      mb: 0.8,
                      bgcolor:
                        pub.type === "Publication"
                          ? "rgba(102,126,234,0.12)"
                          : "rgba(118,75,162,0.12)",
                      color:
                        pub.type === "Publication" ? "#667eea" : "#764ba2",
                      fontWeight: 700,
                      border: `1px solid ${pub.type === "Publication" ? "rgba(102,126,234,0.25)" : "rgba(118,75,162,0.25)"}`,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      color: "var(--body_color)",
                      lineHeight: 1.4,
                      mb: 0.5,
                      fontSize: "0.87rem",
                    }}
                  >
                    {pub.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--body_color)",
                      opacity: 0.55,
                      fontSize: "0.72rem",
                      display: "block",
                    }}
                  >
                    {pub.venue} &nbsp;•&nbsp; {pub.date}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};
